import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// useRoomControl — couche temps réel « métier » de la salle ZCall.
//
// Cette couche est INDÉPENDANTE du transport média (maillage P2P ou serveur
// média SFU). Elle fait circuler, via le canal broadcast + présence du Backend :
//   • la liste des participants en direct (présence) ;
//   • la qualité réseau (latence aller-retour, ping/pong) ;
//   • les droits d'hôte : couper le micro de tout le monde, main levée,
//     autorisation de présenter ;
//   • les sondages (votes) lancés par l'hôte ;
//   • la lecture d'une vidéo YouTube partagée par l'hôte.
//
// Aucune donnée sensible : tout reste dans le salon (broadcast éphémère).
// ─────────────────────────────────────────────────────────────

export interface RoomParticipant {
  id: string;
  name: string;
  isHost: boolean;
  handRaised: boolean;
  publishing: boolean;
  /** Personne dont l'écran est actuellement partagé. */
  presenting: boolean;
}

export interface RoomVote {
  question: string;
  options: string[];
  open: boolean;
}

export interface RoomYoutube {
  url: string;
  playing: boolean;
}

interface LooseChannel {
  on: (type: string, filter: Record<string, unknown>, cb: (arg: { payload?: unknown; key?: string; [k: string]: unknown }) => void) => LooseChannel;
  subscribe: (cb?: (status: string) => void) => LooseChannel;
  send: (args: { type: 'broadcast'; event: string; payload: Record<string, unknown> }) => Promise<unknown>;
  track: (payload: Record<string, unknown>) => Promise<unknown>;
  presenceState: () => Record<string, Array<Record<string, unknown>>>;
  unsubscribe: () => Promise<unknown>;
}

function randomId(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID().slice(0, 12);
  } catch {
    /* repli */
  }
  return `r-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Extrait l'identifiant d'une vidéo YouTube (watch, youtu.be, embed…). */
export function youtubeEmbedUrl(url: string): string | null {
  const u = (url || '').trim();
  if (!u) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtube\.com\/embed\/|youtu\.be\/|youtube\.com\/live\/)([A-Za-z0-9_-]{6,})/,
  ];
  for (const re of patterns) {
    const m = u.match(re);
    if (m && m[1]) return `https://www.youtube.com/embed/${m[1]}?autoplay=1&rel=0`;
  }
  return null;
}

export interface UseRoomControlResult {
  selfId: string;
  participants: RoomParticipant[];
  latencyMs: number | null;
  mutedAll: boolean;
  grantedIds: string[];
  isGranted: boolean;
  handsRaised: RoomParticipant[];
  vote: RoomVote | null;
  voteCounts: number[];
  myVote: number | null;
  youtube: RoomYoutube | null;
  /** Nom de la personne désignée présentatrice (ou qui partage son écran). */
  presenterName: string | null;
  /** L'hôte m'a désigné présentateur : je dois lancer le partage d'écran. */
  presenterRequested: boolean;
  setMutedAll: (value: boolean) => void;
  grant: (id: string) => void;
  grantEveryone: () => void;
  setOwnHand: (raised: boolean) => void;
  startVote: (question: string, options: string[]) => void;
  castVote: (index: number) => void;
  closeVote: () => void;
  setYoutube: (value: RoomYoutube | null) => void;
  /** Désigne un participant comme présentateur (partage d'écran à distance). */
  designatePresenter: (participant: RoomParticipant | null) => void;
  /** Acquitte la demande de présentation (le modal a été traité). */
  acknowledgePresenter: () => void;
}

interface Options {
  roomId: string;
  selfName: string;
  active: boolean;
  isHost: boolean;
  publishing: boolean;
  /** Mon écran est-il en cours de partage ? (→ badge « présente » côté pairs). */
  presenting?: boolean;
}

export function useRoomControl({ roomId, selfName, active, isHost, publishing, presenting = false }: Options): UseRoomControlResult {
  const selfIdRef = useRef<string>('');
  if (!selfIdRef.current) selfIdRef.current = randomId();
  const selfId = selfIdRef.current;

  const channelRef = useRef<LooseChannel | null>(null);
  const [participants, setParticipants] = useState<RoomParticipant[]>([]);
  const [latencyMs, setLatencyMs] = useState<number | null>(null);
  const [mutedAll, setMutedAllState] = useState(false);
  const [grantedIds, setGrantedIds] = useState<string[]>([]);
  const [vote, setVote] = useState<RoomVote | null>(null);
  const [voteVoters, setVoteVoters] = useState<Record<string, number>>({});
  const [youtube, setYoutubeState] = useState<RoomYoutube | null>(null);
  const [presenterName, setPresenterName] = useState<string | null>(null);
  const [presenterRequested, setPresenterRequested] = useState(false);

  const handRef = useRef(false);
  const mutedAllRef = useRef(false);
  const publishingRef = useRef(publishing);
  publishingRef.current = publishing;
  const presentingRef = useRef(presenting);
  presentingRef.current = presenting;
  const isHostRef = useRef(isHost);
  isHostRef.current = isHost;

  const trackState = useCallback(() => {
    const ch = channelRef.current;
    if (!ch) return;
    void ch.track({
      id: selfIdRef.current,
      name: selfName || 'Participant',
      isHost: isHostRef.current,
      handRaised: handRef.current,
      publishing: publishingRef.current,
      presenting: presentingRef.current,
    });
  }, [selfName]);

  const syncParticipants = useCallback(() => {
    const ch = channelRef.current;
    if (!ch) return;
    const state = ch.presenceState();
    const list: RoomParticipant[] = [];
    Object.keys(state).forEach((key) => {
      const metas = state[key] || [];
      const meta = metas[0] || {};
      list.push({
        id: String(meta.id || key),
        name: String(meta.name || 'Participant'),
        isHost: !!meta.isHost,
        handRaised: !!meta.handRaised,
        publishing: !!meta.publishing,
        presenting: !!meta.presenting,
      });
    });
    setParticipants(list);
  }, []);

  // ── Connexion au canal temps réel ──
  useEffect(() => {
    if (!active || !roomId) return undefined;
    let cancelled = false;

    const ch = supabase.channel(`zcall-ctl:${roomId}`, {
      config: { broadcast: { self: false }, presence: { key: selfIdRef.current } },
    }) as unknown as LooseChannel;
    channelRef.current = ch;

    ch.on('broadcast', { event: 'muted-all' }, ({ payload }) => {
      const value = !!(payload as { value?: boolean } | undefined)?.value;
      mutedAllRef.current = value;
      setMutedAllState(value);
      if (!value) setGrantedIds([]);
    })
      .on('broadcast', { event: 'grants' }, ({ payload }) => {
        const ids = ((payload as { ids?: string[] } | undefined)?.ids) || [];
        setGrantedIds(ids);
      })
      .on('broadcast', { event: 'vote-start' }, ({ payload }) => {
        const p = payload as { question?: string; options?: string[] } | undefined;
        setVote({ question: p?.question || '', options: p?.options || [], open: true });
        setVoteVoters({});
      })
      .on('broadcast', { event: 'vote-cast' }, ({ payload }) => {
        const p = payload as { voter?: string; index?: number } | undefined;
        if (!p?.voter || typeof p.index !== 'number') return;
        setVoteVoters((prev) => (prev[p.voter as string] === p.index ? prev : { ...prev, [p.voter as string]: p.index as number }));
      })
      .on('broadcast', { event: 'vote-close' }, () => {
        setVote((v) => (v ? { ...v, open: false } : v));
      })
      .on('broadcast', { event: 'youtube' }, ({ payload }) => {
        const p = payload as { url?: string; playing?: boolean } | null | undefined;
        if (!p?.url) setYoutubeState(null);
        else setYoutubeState({ url: p.url, playing: !!p.playing });
      })
      .on('broadcast', { event: 'presenter' }, ({ payload }) => {
        const p = payload as { id?: string; name?: string } | null | undefined;
        const id = p?.id ? String(p.id) : '';
        if (!id) {
          setPresenterName(null);
          setPresenterRequested(false);
          return;
        }
        setPresenterName(p?.name ? String(p.name) : 'Un participant');
        if (id === selfIdRef.current) setPresenterRequested(true);
      })
      .on('broadcast', { event: 'ping' }, ({ payload }) => {
        const p = payload as { from?: string; t?: number } | undefined;
        if (!p?.from || p.from === selfIdRef.current) return;
        void ch.send({ type: 'broadcast', event: 'pong', payload: { from: selfIdRef.current, to: p.from, t: p.t } });
      })
      .on('broadcast', { event: 'pong' }, ({ payload }) => {
        const p = payload as { to?: string; t?: number } | undefined;
        if (!p || p.to !== selfIdRef.current || typeof p.t !== 'number') return;
        const rtt = Date.now() - p.t;
        setLatencyMs((prev) => (prev == null ? rtt : Math.round(prev * 0.6 + rtt * 0.4)));
      })
      .on('presence', { event: 'sync' }, () => syncParticipants())
      .subscribe((status: string) => {
        if (cancelled) return;
        if (status === 'SUBSCRIBED') {
          trackState();
          syncParticipants();
        }
      });

    return () => {
      cancelled = true;
      try { void ch.unsubscribe(); } catch { /* ignore */ }
      channelRef.current = null;
      setParticipants([]);
    };
  }, [active, roomId, selfId, syncParticipants, trackState]);

  // ── Mise à jour de la présence quand mes infos changent ──
  useEffect(() => {
    trackState();
  }, [selfName, isHost, publishing, presenting, trackState]);

  // ── Mesure de latence : ping périodique ──
  useEffect(() => {
    if (!active || !roomId) return undefined;
    const tick = () => {
      const ch = channelRef.current;
      if (!ch) return;
      void ch.send({ type: 'broadcast', event: 'ping', payload: { from: selfIdRef.current, t: Date.now() } });
    };
    tick();
    const interval = window.setInterval(tick, 4000);
    return () => window.clearInterval(interval);
  }, [active, roomId]);

  const send = useCallback((event: string, payload: Record<string, unknown>) => {
    const ch = channelRef.current;
    if (!ch) return;
    void ch.send({ type: 'broadcast', event, payload });
  }, []);

  const setMutedAll = useCallback((value: boolean) => {
    mutedAllRef.current = value;
    setMutedAllState(value);
    if (!value) setGrantedIds([]);
    send('muted-all', { value });
    send('grants', { ids: value ? [] : [] });
  }, [send]);

  const broadcastGrants = useCallback((ids: string[]) => {
    setGrantedIds(ids);
    send('grants', { ids });
  }, [send]);

  const grant = useCallback((id: string) => {
    setGrantedIds((prev) => {
      const next = prev.includes(id) ? prev : [...prev, id];
      send('grants', { ids: next });
      return next;
    });
  }, [send]);

  const grantEveryone = useCallback(() => {
    const ids = participants.map((p) => p.id);
    broadcastGrants(ids);
  }, [participants, broadcastGrants]);

  const setOwnHand = useCallback((raised: boolean) => {
    handRef.current = raised;
    trackState();
  }, [trackState]);

  const startVote = useCallback((question: string, options: string[]) => {
    const clean = options.map((o) => o.trim()).filter(Boolean);
    if (!question.trim() || clean.length < 2) return;
    setVote({ question: question.trim(), options: clean, open: true });
    setVoteVoters({});
    send('vote-start', { question: question.trim(), options: clean });
  }, [send]);

  const castVote = useCallback((index: number) => {
    send('vote-cast', { voter: selfIdRef.current, index });
    setVoteVoters((prev) => ({ ...prev, [selfIdRef.current]: index }));
  }, [send]);

  const closeVote = useCallback(() => {
    setVote((v) => (v ? { ...v, open: false } : v));
    send('vote-close', {});
  }, [send]);

  const setYoutube = useCallback((value: RoomYoutube | null) => {
    setYoutubeState(value);
    if (!value) send('youtube', {});
    else send('youtube', { url: value.url, playing: value.playing });
  }, [send]);

  // ── Présentateur : l'hôte désigne qui doit partager son écran. ──
  const designatePresenter = useCallback((participant: RoomParticipant | null) => {
    if (!participant) {
      setPresenterName(null);
      send('presenter', {});
      return;
    }
    setPresenterName(participant.name);
    send('presenter', { id: participant.id, name: participant.name });
  }, [send]);

  const acknowledgePresenter = useCallback(() => setPresenterRequested(false), []);

  const voteCounts = vote ? vote.options.map((_, i) => Object.values(voteVoters).filter((v) => v === i).length) : [];
  const myVote = voteVoters[selfIdRef.current] ?? null;
  const handsRaised = participants.filter((p) => p.handRaised && !p.isHost);
  const isGranted = !mutedAll || grantedIds.includes(selfIdRef.current);

  return {
    selfId,
    participants,
    latencyMs,
    mutedAll,
    grantedIds,
    isGranted,
    handsRaised,
    vote,
    voteCounts,
    myVote,
    youtube,
    presenterName,
    presenterRequested,
    setMutedAll,
    grant,
    grantEveryone,
    setOwnHand,
    startVote,
    castVote,
    closeVote,
    setYoutube,
    designatePresenter,
    acknowledgePresenter,
  };
}